import { useEffect, useState } from 'react'
import type { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { type CertificatePriceLimit, type FreeTrial } from '@mc/api'
import { PlansSelectionPage, i18nInstance } from '@mc/checkout'
import { TrialNoLongerAvailableModal } from '@mc/free-trial'
import { I18nextProvider } from '@mc/i18n'
import { getBucket } from '@mc/next-growthbook'
import { getUserAttributes } from '@mc/next-user-attributes'
import { paths } from '@mc/paths'
import {
  savePlansPageViewed,
  trackPlansPageViewed,
} from '@mc/pre-paywall-events'
import { PROMO_FEATURE_FLAG } from '@mc/promo-utils'
import { StaticDataProvider, useStaticData } from '@mc/static-data-react'
import { PageLoadEvents, trackPage } from '@mc/track-event'
import { SimplifiedFooter } from 'components/Footer/SimplifiedFooter'
import { useTrackPen7387Experiment } from 'experiments/pen-7387_v1_remove_plans_primer'
import {
  isPen7387Variant,
  trackPen7387BackendEV,
} from 'experiments/pen-7387_v1_remove_plans_primer/server'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { getCertificatePriceLimit } from 'experiments/prcng-698_v1_bundling_toolkit_and_certificates'
import { TrackDecreaseStandardPriceEV } from 'experiments/prcng-861_v1_decrease_standard_price'
import {
  type CertsValue,
  getServerSideCertsParam,
} from 'experiments/prcng-1138_v1_certs_quiz_plans'
import { getCertsAddOnPriceLimit } from 'experiments/prcng-1138_v1_certs_quiz_plans/server'
import { getServerSidePlanParam } from 'experiments/prcng-1266_v1_cm_dual_cta'
import { trackPrcng1270BackendEV } from 'experiments/prcng-1270_v1_mc_pro_promo/server'
import { getFreeTrialByToken } from 'libs/server/api'
import {
  DisplayProduct,
  getCurrentTierCollection,
  getDefaultTier,
  type TiersMetadata,
  type DisplayTier,
} from 'libs/server/api/tiers'
import { getServerSideDiscountSlugCookie } from 'libs/server/cookie/getServerSideDiscountSlugCookie'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { redirect } from 'libs/utils/redirect'
import { PageComponent } from 'types/components/pages'
import { PlansPrimerPage } from './components/PlansPrimerPage'
import { useTrackCheckoutBackExit } from './hooks'
import { getServerSideGiftParam } from './utils/getServerSideGiftParam'
import { validateTiersDiscount } from './utils/validateTiersDiscount'

export type CheckoutPageProps = {
  tiers: DisplayTier[]
  tiersMetadata: TiersMetadata
  trial: FreeTrial
  isGift: boolean
  skipPrimer: boolean
  annualPass: DisplayProduct | null
  certificatePriceLimit: CertificatePriceLimit | null // prcng-698_v1
  certsAddOnPriceLimit: CertificatePriceLimit | null // prcng-1138_v1
  certsValue: CertsValue | null // prcng-1138_v1
  preselectedPlan: ReturnType<typeof getServerSidePlanParam> // prcng-1266_v1
}

const BelowTheFold = dynamic(
  () => import('./components/BelowTheFold').then((c) => c.BelowTheFold),
  {
    ssr: false,
  },
)

const CheckoutPage: PageComponent<CheckoutPageProps> = ({
  tiers,
  tiersMetadata,
  trial,
  isGift,
  skipPrimer,
  annualPass,
  certificatePriceLimit,
  certsAddOnPriceLimit,
  certsValue,
  preselectedPlan,
}: CheckoutPageProps) => {
  const { query } = useRouter()
  const { isLoggedIn } = useStaticData<{ isLoggedIn: boolean }>()
  const [showPlansPrimerPage, setShowPlansPrimerPage] = useState(
    !isGift && !skipPrimer,
  )

  useEffect(() => {
    if (!showPlansPrimerPage) {
      trackPage(PageLoadEvents.PLANS, {
        platform: 'next',
        src: query.src,
        is_free_trial: trial?.eligible,
        partnership: trial?.partnership,
        free_trial_type: trial?.freeTrialType,
      })
    }
  }, [showPlansPrimerPage, query.src, trial])

  useEffect(() => {
    trackPlansPageViewed({ isLoggedIn })
  }, [isLoggedIn])

  useEffect(() => {
    savePlansPageViewed()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savePlansPageViewed])

  useTrackCheckoutBackExit()
  useTrackPen7387Experiment()

  return (
    <StaticDataProvider
      value={{
        tiers,
        tiersMetadata,
        trial,
        isGift,
        annualPass,
        certificatePriceLimit, // prcng-698_v1
        certsAddOnPriceLimit, // prcng-1138_v1
        certsValue, // prcng-1138_v1
        preselectedPlan, // prcng-1266_v1
      }}
    >
      <I18nextProvider i18n={i18nInstance} defaultNS='@mc/next'>
        <TrialNoLongerAvailableModal />
        <StandardCheckoutContent
          showPlansPrimerPage={showPlansPrimerPage}
          onDismissPrimer={() => setShowPlansPrimerPage(false)}
        />
      </I18nextProvider>
    </StaticDataProvider>
  )
}

const StandardCheckoutContent = ({
  showPlansPrimerPage,
  onDismissPrimer,
}: {
  showPlansPrimerPage: boolean
  onDismissPrimer: () => void
}) =>
  showPlansPrimerPage ? (
    <PlansPrimerPage dismiss={onDismissPrimer} />
  ) : (
    <PlansSelectionPage>
      <TrackDecreaseStandardPriceEV />
      <BelowTheFold />
    </PlansSelectionPage>
  )

CheckoutPage.pageSettings = {
  navigation: null,
  footer: SimplifiedFooter,
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const {
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  const discountSlug = await getServerSideDiscountSlugCookie(ctx)
  const isGift = getServerSideGiftParam(ctx)
  const promoBucket = await getBucket(ctx, PROMO_FEATURE_FLAG)
  const { query, req } = ctx
  const userAttributes = getUserAttributes(ctx, { query })
  const { bogo_available: bogoAvailable } = userAttributes
  const isMemberUser = req.cookies.user_membership === 'AAP'

  if (isMemberUser && !isGift) {
    // Redirect to homepage because the user has already a subscription
    return redirect(ctx, paths.home, { queryExclude: ['product_id'] })
  }

  const isEnterpriseUser = req.cookies.user_membership === 'ENTERPRISE'
  if (isEnterpriseUser) {
    return redirect(ctx, paths.home, { queryExclude: ['product_id', 'gift'] })
  }

  let tiers: DisplayTier[]
  let tiersMetadata: TiersMetadata

  try {
    const tierCollection = await getCurrentTierCollection(ctx, {
      discountSlug,
      gift: isGift,
      bogoAvailable,
    })
    tiers = tierCollection.tiers
    tiersMetadata = tierCollection.metadata

    // logging to detect incorrect discounts for promos
    await validateTiersDiscount({
      ctx,
      tiers,
      isGift,
      promoBucket,
      discountSlug,
      userAttributes,
    })
  } catch (e) {
    return pageErrorHandler(e)
  }

  await trackPen7387BackendEV(ctx)
  await trackPen7565BackendEV(ctx, { isGift })
  await trackPen7584BackendEV(ctx, { isGift })
  await trackPrcng1270BackendEV(ctx, { isGift })
  const skipPrimerForPen7387 = await isPen7387Variant(ctx)

  return getPageData({
    nonCriticalPromises: {
      trial: getFreeTrialByToken(ctx, { referralToken }),
      certificatePriceLimit: getCertificatePriceLimit(ctx, tiersMetadata), // prcng-698_v1
      certsAddOnPriceLimit: getCertsAddOnPriceLimit(ctx), // prcng-1138_v1
    },
    propsMapper: (restProps) => ({
      tiers,
      tiersMetadata,
      isGift,
      skipPrimer: skipPrimerForPen7387,
      userAttributes,
      annualPass: getDefaultTier(tiers)?.product,
      certsValue: getServerSideCertsParam(ctx),
      preselectedPlan: getServerSidePlanParam(ctx), // prcng-1266_v1
      ...restProps,
    }),
  })
}

export default CheckoutPage
